// src/hooks/useDingOnPedido.js
import { useEffect, useRef, useState } from "react";

/**
 * Toca o ding quando pedido for > 0.
 * - Se o autoplay for bloqueado, agenda retry a cada retryMs enquanto pedido > 0.
 * - Quando pedido volta a 0 (ou menos), cancela o retry. Se voltar a > 0, tenta de novo.
 *
 * @param {number} pedido
 * @param {string} src caminho do mp3 (ex.: "/ding.mp3" ou importado de assets)
 * @param {object} options
 *  - retryMs: intervalo entre tentativas automáticas (default 10000 = 10s)
 *  - maxRetries: limite de tentativas por “fase positiva” (default Infinity)
 *  - muted: não toca (e cancela retries) enquanto true
 */
export function useDingOnPedido(
  pedido,
  src = "/ding.mp3",
  { retryMs = 10000, maxRetries = Infinity, muted = false } = {}
) {
  const audioRef = useRef(null);
  const mutedRef = useRef(muted);
  mutedRef.current = muted;
  const retryTimerRef = useRef(null);
  const retryCountRef = useRef(0);

  const [needsUserAction, setNeedsUserAction] = useState(false);
  const [lastError, setLastError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  // cria o player uma vez (ou quando src mudar)
  useEffect(() => {
    const a = new Audio(src);
    a.preload = "auto";
    a.volume = 1.0;
    audioRef.current = a;
    return () => {
      clearRetryTimer();
      try {
        a.pause();
        a.src = "";
      } catch {}
      audioRef.current = null;
    };
  }, [src]);

  const clearRetryTimer = () => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  };

  const scheduleRetry = () => {
    if (retryCountRef.current >= maxRetries) return;
    clearRetryTimer();
    retryTimerRef.current = setTimeout(() => {
      retryPlay();
    }, retryMs);
  };

  const resetRetryState = () => {
    clearRetryTimer();
    retryCountRef.current = 0;
    setRetryCount(0);
    setNeedsUserAction(false);
    setLastError(null);
  };

  const retryPlay = async () => {
    const a = audioRef.current;
    if (!a) return;
    if (mutedRef.current) {
      resetRetryState();
      return;
    }
    try {
      a.currentTime = 0;
      await a.play();
      // sucesso: limpa erros e para os retries
      resetRetryState();
    } catch (err) {
      setNeedsUserAction(true);
      setLastError(err?.message || String(err));
      retryCountRef.current += 1;
      setRetryCount(retryCountRef.current);
      scheduleRetry();
    }
  };

  // Reage a mudanças de "pedido"
  useEffect(() => {
    if (pedido > 0 && !muted) {
      // Sempre que ficar > 0, tenta tocar imediatamente
      retryPlay();
    } else {
      // Se não for > 0, cancela qualquer retry pendente
      resetRetryState();
      if (muted) audioRef.current?.pause();
    }
  }, [pedido, muted]);

  // Botão para o usuário tentar manualmente (se autoplay bloquear)
  const manualRetryPlay = () => retryPlay();

  return {
    needsUserAction, // true se o navegador bloqueou autoplay
    lastError, // texto do último erro (se houver)
    retryCount, // tentativas feitas nessa “fase” (pedido > 0)
    retryPlay: manualRetryPlay,
    cancelAutoRetry: clearRetryTimer,
  };
}
